import React from 'react';

import Modal from 'react-modal';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { func, shape, bool, arrayOf, string } from 'prop-types';

import Theme from '../../../../theme/Theme';
import {
  CloseIcon,
  RecurringIcon,
  DspOnlyIcon,
} from '../../../../theme/images';
import { ModalBox, Button, PageLoader } from '../../../../common';
import { HYBRID } from '../../../../constants/CommanVariableConstants';

const AddNewContractModal = ({
  customStyles,
  showAddContractModal,
  setShowAddContractModal,
  typeOfNewAgreement,
  existingContracts,
  confirmContract,
  contractLoader,
  setContractLoader,
  showYesNoButtons,
  highlightedMarketplaces,
}) => {
  const handleConfirm = () => {
    setContractLoader(true);
    confirmContract();
  };

  const closeModal = () => {
    setShowAddContractModal({
      agreement: showAddContractModal?.agreement,
      show: false,
    });
  };
  const renderExistigContracts = () => {
    return existingContracts.map((contract) => {
      const additionalMarketplaces =
        contract.contract_type === 'dsp only'
          ? contract.dsp_marketplaces
          : contract.additional_marketplaces;

      const marketplaceList = Array.isArray(additionalMarketplaces)
        ? additionalMarketplaces
        : [];

      let displayedMarketplaces = marketplaceList;
      if (contract?.seller_type?.value === HYBRID) {
        const seenMarketplaces = new Set([contract?.primary_marketplace?.name]);
        displayedMarketplaces = marketplaceList.filter((marketplace) => {
          if (seenMarketplaces.has(marketplace?.name)) return false;
          seenMarketplaces.add(marketplace?.name);
          return true;
        });
      }
      return (
        <OrangeFieldSet className="mt-4" key={contract.id}>
          <CheckBoxContract>
            <div className="checkbox" role="presentation">
              <label htmlFor={contract.id}>
                <div className="solid-icon mt-2">
                  <img
                    width="48px"
                    className="solid-icon"
                    src={
                      typeOfNewAgreement.value === 'recurring' ||
                      typeOfNewAgreement?.value === 'recurring (90 day notice)'
                        ? RecurringIcon
                        : DspOnlyIcon
                    }
                    alt="sync"
                  />
                </div>
                <div className="contract-status" role="presentation">
                  <p className="black-heading-title mt-2 pt-1 mb-0">
                    {typeOfNewAgreement.label}
                  </p>
                  <ul className="recurring-contact">
                    <li>
                      <p className="basic-text ">
                        Expires{' '}
                        {dayjs(contract.end_date).format('MMM DD, YYYY')}
                      </p>
                    </li>
                    <li>
                      <span className="dot" />
                      <p className="basic-text ">
                        {contract?.primary_marketplace?.name ? (
                          <MatchedMarketplace
                            $matched={highlightedMarketplaces.includes(
                              contract.primary_marketplace.name,
                            )}
                          >
                            {contract.primary_marketplace.name}{' '}
                            &#40;Primary&#41;
                          </MatchedMarketplace>
                        ) : null}

                        {displayedMarketplaces.map((marketplace) => (
                          <React.Fragment key={marketplace.id}>
                            ,{' '}
                            <MatchedMarketplace
                              $matched={highlightedMarketplaces.includes(
                                marketplace.name,
                              )}
                            >
                              {marketplace.name}
                            </MatchedMarketplace>
                          </React.Fragment>
                        ))}
                      </p>
                    </li>
                  </ul>
                </div>
              </label>
            </div>
          </CheckBoxContract>
          <div className="clear-fix" />
        </OrangeFieldSet>
      );
    });
  };

  return (
    <Modal
      isOpen={showAddContractModal?.show}
      style={customStyles}
      ariaHideApp={false}
      contentLabel="Edit modal"
    >
      <img
        src={CloseIcon}
        alt="close"
        className="float-right cursor cross-icon"
        onClick={closeModal}
        role="presentation"
      />
      <ModalBox>
        <div className="modal-body">
          <h4 className="on-boarding ">
            ADD {typeOfNewAgreement.label}{' '}
            {typeOfNewAgreement.sub ? (
              <>&#60;{typeOfNewAgreement.sub}&#62;</>
            ) : null}
          </h4>{' '}
          {showYesNoButtons ? (
            <p className="long-text-agr">
              Primary marketplaces cannot be removed from existing contracts.
              Additional marketplaces require an addendum for removal before a
              new contract can be created. Signing a new contract will cancel
              the existing contract.
            </p>
          ) : (
            <p className="long-text-agr">
              If you create a new agreement of the same marketplace, the
              previous agreement for that marketplace will be cancelled.
            </p>
          )}
          <p className="long-text-agr">
            You currently have {existingContracts?.length} active agreements
            with the {showYesNoButtons ? 'common' : 'given'} marketplaces.{' '}
          </p>
          {renderExistigContracts()}
          {showYesNoButtons && (
            <div className="long-text-agr">
              {' '}
              <p>Do you want to proceed?</p>
              <span>
                Yes: To create this new contract to override existing agreement.
              </span>
              <br />
              <span>
                No: To exit and raise an addendum to remove the additional
                marketplace.
              </span>
            </div>
          )}
          {showYesNoButtons ? (
            <div className="row mt-4">
              <div className="col-6">
                <Button className="btn-primary w-100" onClick={handleConfirm}>
                  {contractLoader ? (
                    <PageLoader color="#fff" type="button" />
                  ) : (
                    <>Yes</>
                  )}
                </Button>
              </div>
              <div className="col-6">
                <Button className="btn-transparent w-100" onClick={closeModal}>
                  No
                </Button>
              </div>
            </div>
          ) : (
            <Button className="btn-primary w-100 mt-4" onClick={handleConfirm}>
              {contractLoader ? (
                <PageLoader color="#fff" type="button" />
              ) : (
                <>Confirm</>
              )}
            </Button>
          )}
        </div>
      </ModalBox>
    </Modal>
  );
};

export default AddNewContractModal;

AddNewContractModal.defaultProps = {
  existingContracts: [],
  showYesNoButtons: false,
  highlightedMarketplaces: [],
};

AddNewContractModal.propTypes = {
  customStyles: shape({}).isRequired,
  showAddContractModal: bool.isRequired,
  setShowAddContractModal: func.isRequired,
  typeOfNewAgreement: shape({}).isRequired,
  existingContracts: arrayOf(shape({})),
  confirmContract: func.isRequired,
  contractLoader: bool.isRequired,
  setContractLoader: func.isRequired,
  showYesNoButtons: bool,
  highlightedMarketplaces: arrayOf(string),
};

const MatchedMarketplace = styled.span`
  color: ${({ $matched }) => ($matched ? Theme.baseColor : 'inherit')};
`;

const OrangeFieldSet = styled.div`
  border-radius: 4px;
  border: 1px solid ${Theme.gray11};
  padding: 0 11px 8px 11px;
  cursor: pointer;
  &.active {
    border: 1px solid ${Theme.orange};
  }
`;

const CheckBoxContract = styled.div`
  label {
    cursor: pointer;
  }

  .checkbox input:checked ~ label:before {
    background-repeat: no-repeat;
    z-index: 2;
    background-position-y: 8px;
    background-position-x: 8px;
    content: '';
  }
  .checkbox input {
    display: none;
  }

  .checkbox label:before {
    margin-top: 25px;
    width: 6px;
    float: right;
    height: 11px;
    border: solid ${Theme.orange};
    border-width: 0 2px 2px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }
`;
